import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styles from "./Exchange.module.css";
import {
  selectYears,
  selectBrands,
  selectModels,
  selectVariants,
  selectYearsLoading,
  selectBrandsLoading,
  selectModelsLoading,
  selectVariantsLoading,
  selectYearsError,
  selectBrandsError,
  selectModelsError,
  selectVariantsError,
  clearBrands,
  clearModels,
  clearVariants,
  clearAll,
  fetchYears,
  fetchBrandsAndClearRest,
  fetchModelsAndClearVariants,
  fetchVariantsForModel,
  postExchange,
} from "./ExchangeSlice";

import { changeNumberFormat } from "../../utils";
import { selectModelData, loadModelFromAPI } from "../model/modelSlice";
import { selectUserData } from "../login/loginSlice";
import LoginPopUp from "../login/LoginPopUp";
import { SimpleImage } from "../../components/atoms/Image/Image";
import { selectLocationData } from "../../components/molecules/Location/locationSlice";
import { Helmet } from "react-helmet";

const Exchange = () => {
  const dispatch = useDispatch();
  const { productId } = useParams();

  const years = useSelector(selectYears);
  const brands = useSelector(selectBrands);
  const models = useSelector(selectModels);
  const variants = useSelector(selectVariants);
  const loadingYears = useSelector(selectYearsLoading);
  const loadingBrands = useSelector(selectBrandsLoading);
  const loadingModels = useSelector(selectModelsLoading);
  const loadingVariants = useSelector(selectVariantsLoading);
  const yearsError = useSelector(selectYearsError);
  const brandsError = useSelector(selectBrandsError);
  const modelsError = useSelector(selectModelsError);
  const variantsError = useSelector(selectVariantsError);
  const product = useSelector(selectModelData);
  const userData = useSelector(selectUserData);
  const location = useSelector(selectLocationData);

  const [year, setYear] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedVariant, setSelectedVariant] = useState("");
  const [selectedNewVariant, setSelectedNewVariant] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [noOfOwners, setNoOfOwners] = useState("1");
  const [kmsDriven, setKmsDriven] = useState("");
  const [insuranceType, setInsuranceType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginForm, setLoginForm] = useState(false);

  useEffect(() => {
    dispatch(fetchYears());
    return () => {
      dispatch(clearAll());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      loadModelFromAPI(
        productId,
        location?.lat &&
          location?.lng && {
            lat: location?.lat,
            lng: location?.lng,
          }
      )
    );
  }, [dispatch, productId, location.lat, location.lng]);

  const insuranceTypes = [
    { label: "Comprehensive Insurance", value: "comprehensive" },
    { label: "Third Party Insurance", value: "third_party" },
  ];

  const CustomDropdown = ({
    data = [],
    selectedValue,
    onValueChange,
    placeholder = "Select an option",
    disabled = false,
  }) => {
    const [isOpen, setIsOpen] = useState(false);

    const selectedItem = data.find((item) => item.value === selectedValue);

    const handleSelect = (item) => {
      onValueChange(item.value);
      setIsOpen(false);
    };

    return (
      <div className={styles.dropdownContainer}>
        <button
          type="button"
          className={`${styles.dropdownButton} ${
            disabled ? styles.disabledButton : ""
          }`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <span
            className={
              selectedItem ? styles.selectedText : styles.placeholderText
            }
          >
            {selectedItem ? selectedItem.label : placeholder}
          </span>
          <span className={styles.arrow}>▼</span>
        </button>

        {isOpen && (
          <div className={styles.dropdownModal}>
            {data.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`${styles.dropdownOption} ${
                  selectedValue === item.value ? styles.selectedOption : ""
                }`}
                onClick={() => handleSelect(item)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const setYearHandler = (e) => {
    const selectedYear = e.target.value;
    setYear(selectedYear);
    setSelectedBrand("");
    setSelectedModel("");
    setSelectedVariant("");

    if (selectedYear) {
      dispatch(fetchBrandsAndClearRest({ year: selectedYear }));
    } else {
      dispatch(clearBrands());
      dispatch(clearModels());
      dispatch(clearVariants());
    }
  };

  const handleBrandChange = (brandId) => {
    setSelectedBrand(brandId);
    setSelectedModel("");
    setSelectedVariant("");

    if (brandId) {
      dispatch(fetchModelsAndClearVariants({ year, brand: brandId }));
    } else {
      dispatch(clearModels());
      dispatch(clearVariants());
    }
  };

  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    setSelectedVariant("");

    if (modelId) {
      dispatch(fetchVariantsForModel({ model: modelId, year }));
    } else {
      dispatch(clearVariants());
    }
  };

  const resetForm = useCallback(() => {
    setYear("");
    setSelectedBrand("");
    setSelectedModel("");
    setSelectedVariant("");
    setRegistrationNumber("");
    setNoOfOwners("1");
    setKmsDriven("");
    setInsuranceType("");
    setSelectedNewVariant("");
    dispatch(clearAll());
    dispatch(fetchYears());
  }, [dispatch]);

  const isFormValid =
    year &&
    selectedBrand &&
    selectedModel &&
    selectedVariant &&
    selectedNewVariant &&
    registrationNumber &&
    noOfOwners &&
    kmsDriven &&
    insuranceType &&
    product?.dealer?.id &&
    product?.showroom?.id;

  const handleSubmit = useCallback(async () => {
    if (!isFormValid) {
      alert("Please fill in all required fields");
      return;
    }

    if (!product?.dealer?.id || !product?.showroom?.id) {
      alert(
        "Product dealer or showroom information is missing. Please try again."
      );
      return;
    }

    if (!selectedNewVariant) {
      alert("Please select a new variant");
      return;
    }

    setIsSubmitting(true);

    try {
      const exchangeData = {
        year: year,
        model_id: product?.id,
        variant_id: selectedNewVariant,
        dealer_id: product?.dealer?.id,
        showroom_id: product?.showroom?.id,
        ex_model_id: selectedModel,
        ex_variant_id: selectedVariant,
        registration_number: registrationNumber,
        no_of_owners: parseInt(noOfOwners),
        kms_driven: parseInt(kmsDriven.replace(/,/g, "")),
        insurance_type: insuranceType,
      };

      console.log("Submitting exchange data:", exchangeData);

      const exchange = await dispatch(postExchange(exchangeData));
      if (exchange && exchange.id) {
        alert("Exchange details submitted successfully!");
        resetForm();
      } else {
        const errorMessage =
          exchange?.message?.error ||
          exchange?.message ||
          "Failed to submit exchange details";
        alert(errorMessage);
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert("Failed to submit exchange details. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isFormValid,
    year,
    product?.id,
    product?.dealer?.id,
    product?.showroom?.id,
    selectedNewVariant,
    selectedModel,
    selectedVariant,
    registrationNumber,
    noOfOwners,
    kmsDriven,
    insuranceType,
    resetForm,
    dispatch,
  ]);

  const LoginHandler = () => {
    setLoginForm(true);
  };

  useEffect(() => {
    if (loginForm && userData?.session && userData.session["access-token"]) {
      setLoginForm(false);
      handleSubmit();
    }
  }, [userData, loginForm, handleSubmit]);

  const validateKmsDriven = (kms) => {
    const kmsNum = parseInt(kms, 10);
    if (!kms) return "Kilometers driven is required";
    if (isNaN(kmsNum)) return "Please enter a valid number";
    if (kmsNum < 0) return "Cannot be negative";
    if (kmsNum > 999999) return "Maximum 999,999 km allowed";
    return "";
  };

  const formatRegistrationNumber = (text) => {
    return text.replace(/\s/g, "").toUpperCase();
  };

  const formatKmsDriven = (text) => {
    const num = text.replace(/,/g, "");
    if (isNaN(num) || num === "") return "";
    return parseInt(num).toLocaleString();
  };

  const handleOwnersIncrement = () => {
    const newValue = Math.min(parseInt(noOfOwners) + 1, 10);
    setNoOfOwners(newValue.toString());
  };

  const handleOwnersDecrement = () => {
    const newValue = Math.max(parseInt(noOfOwners) - 1, 1);
    setNoOfOwners(newValue.toString());
  };

  const handleOwnersTextChange = (e) => {
    const text = e.target.value;
    const numValue = parseInt(text) || 1;
    if (numValue >= 1 && numValue <= 10) {
      setNoOfOwners(text);
    }
  };

  if (!product?.id) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <span className={styles.loadingText}>Loading product...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <title>
          Exchange your car to a new {product.name} | Best Exchange Offers |
          Carbae
        </title>
        <meta
          name="description"
          content={` Exchange your old car to a new ${product.name} with the best exchange offers, additional brand incentives, free evaluation, and a smooth process on Carbae `}
        />
        <meta
          name="keywords"
          content={`${product.name} Exchange ,carbae,${product.name} Exchange carbae,${product.name} Exchange near me`}
        />
      </Helmet>

      {loginForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <div></div>
              <button
                onClick={() => setLoginForm(false)}
                className={styles.closeButton}
              >
                ✕
              </button>
            </div>
            <h2 className={styles.loginHeading}>Log in to proceed.</h2>
            <LoginPopUp />
          </div>
        </div>
      )}

      <div className={styles.sectionContainer}>
        <div className={styles.leftsection}>
          <div className={styles.sticky}>
            <SimpleImage
              className={styles.image}
              src={product?.main_image}
              alt={product.name}
            />

            <div className={styles.nameContainer}>
              <h1 className={styles.heading}>{product?.name || "Car Model"}</h1>
            </div>

            <div className={styles.info}>
              {product?.price_starts && product?.price_ends && (
                <p className={styles.price}>
                  {changeNumberFormat(product?.price_starts)} to{" "}
                  {changeNumberFormat(product?.price_ends)}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className={styles.rightsection}>
          <div className={styles.scroll}>
            <p className={styles.formTitle}>
              To explore exchange options, please fill in the details below.
            </p>

            <div className={styles.formContainer}>
              {(!product?.dealer?.id || !product?.showroom?.id) && (
                <div className={styles.fieldContainer}>
                  <p className={styles.error}>
                    Note: Dealer or showroom information is not available for
                    this product. Please contact support if this issue persists.
                  </p>
                </div>
              )}

              <div className={styles.fieldContainer}>
                <label className={styles.label}>New Variant*</label>
                <select
                  value={selectedNewVariant}
                  onChange={(e) => setSelectedNewVariant(e.target.value)}
                  className={styles.select}
                >
                  <option disabled value="">
                    Select New Variant
                  </option>
                  {product &&
                    !!product?.variants?.length &&
                    product?.variants?.map((variant) => (
                      <option key={variant.id} value={variant.id}>
                        {variant.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className={styles.fieldContainer}>
                <label className={styles.label}>Year Of Manufacture*</label>
                {loadingYears ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <span className={styles.loadingText}>Loading years...</span>
                  </div>
                ) : (
                  <select
                    value={year}
                    onChange={(e) => setYearHandler(e)}
                    className={styles.select}
                  >
                    <option disabled value="">
                      Select Year
                    </option>
                    {years?.map((yearItem) => (
                      <option key={yearItem.id} value={yearItem.year}>
                        {yearItem.year}
                      </option>
                    ))}
                  </select>
                )}
                {yearsError?.haveError && (
                  <p className={styles.error}>{yearsError.message}</p>
                )}
              </div>

              <div className={styles.fieldContainer}>
                <label className={styles.label}>Old Brand*</label>
                {loadingBrands ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <span className={styles.loadingText}>
                      Loading brands...
                    </span>
                  </div>
                ) : (
                  <select
                    value={selectedBrand}
                    onChange={(e) => handleBrandChange(e.target.value)}
                    disabled={!year || loadingBrands}
                    className={`${styles.select} ${
                      !year ? styles.disabledSelect : ""
                    }`}
                  >
                    <option disabled value="">
                      {year ? "Select Brand" : "Select Year First"}
                    </option>
                    {brands?.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                )}
                {brandsError?.haveError && (
                  <p className={styles.error}>{brandsError.message}</p>
                )}
              </div>

              <div className={styles.fieldContainer}>
                <label className={styles.label}>Old Model*</label>
                {loadingModels ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <span className={styles.loadingText}>
                      Loading models...
                    </span>
                  </div>
                ) : (
                  <select
                    value={selectedModel}
                    onChange={(e) => handleModelChange(e.target.value)}
                    disabled={!selectedBrand || loadingModels}
                    className={`${styles.select} ${
                      !selectedBrand ? styles.disabledSelect : ""
                    }`}
                  >
                    <option disabled value="">
                      {selectedBrand ? "Select Model" : "Select Brand First"}
                    </option>
                    {models?.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                )}
                {modelsError?.haveError && (
                  <p className={styles.error}>{modelsError.message}</p>
                )}
              </div>

              <div className={styles.fieldContainer}>
                <label className={styles.label}>Old Variant*</label>
                {loadingVariants ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <span className={styles.loadingText}>
                      Loading variants...
                    </span>
                  </div>
                ) : (
                  <select
                    value={selectedVariant}
                    onChange={(e) => setSelectedVariant(e.target.value)}
                    disabled={!selectedModel || loadingVariants}
                    className={`${styles.select} ${
                      !selectedModel ? styles.disabledSelect : ""
                    }`}
                  >
                    <option disabled value="">
                      {selectedModel ? "Select Variant" : "Select Model First"}
                    </option>
                    {variants?.map((variant) => (
                      <option key={variant.id} value={variant.id}>
                        {variant.name}
                      </option>
                    ))}
                  </select>
                )}
                {variantsError?.haveError && (
                  <p className={styles.error}>{variantsError.message}</p>
                )}
              </div>

              <div className={styles.fieldContainer}>
                <label className={styles.label}>Registration Number*</label>
                <input
                  type="text"
                  value={registrationNumber}
                  onChange={(e) =>
                    setRegistrationNumber(
                      formatRegistrationNumber(e.target.value)
                    )
                  }
                  placeholder="XX00XX0000"
                  maxLength={13}
                  className={styles.textInput}
                  style={{ textTransform: "uppercase" }}
                />
              </div>

              <div className={styles.fieldContainer}>
                <label className={styles.label}>Number of Owners*</label>
                <div className={styles.numberInputContainer}>
                  <button
                    type="button"
                    onClick={handleOwnersDecrement}
                    disabled={parseInt(noOfOwners) <= 1}
                    className={`${styles.numberButton} ${
                      parseInt(noOfOwners) <= 1 ? styles.disabledButton : ""
                    }`}
                  >
                    −
                  </button>

                  <input
                    type="text"
                    value={noOfOwners}
                    onChange={handleOwnersTextChange}
                    maxLength={2}
                    className={styles.numberInput}
                  />

                  <button
                    type="button"
                    onClick={handleOwnersIncrement}
                    disabled={parseInt(noOfOwners) >= 10}
                    className={`${styles.numberButton} ${
                      parseInt(noOfOwners) >= 10 ? styles.disabledButton : ""
                    }`}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className={styles.fieldContainer}>
                <label className={styles.label}>Kilometers Driven*</label>
                <input
                  type="text"
                  value={kmsDriven}
                  onChange={(e) => {
                    const numOnly = e.target.value.replace(/[^0-9]/g, "");
                    setKmsDriven(formatKmsDriven(numOnly));
                  }}
                  placeholder="e.g., 50,000"
                  className={`${styles.textInput} ${
                    validateKmsDriven(kmsDriven.replace(/,/g, ""))
                      ? styles.inputError
                      : ""
                  }`}
                />
                {validateKmsDriven(kmsDriven.replace(/,/g, "")) && (
                  <p className={styles.error}>
                    {validateKmsDriven(kmsDriven.replace(/,/g, ""))}
                  </p>
                )}
              </div>

              <div className={styles.fieldContainer}>
                <label className={styles.label}>Insurance Type*</label>
                <CustomDropdown
                  data={insuranceTypes}
                  selectedValue={insuranceType}
                  onValueChange={setInsuranceType}
                  placeholder="Select Insurance Type"
                />
              </div>

              {userData?.session && userData.session["access-token"] ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting}
                  className={`${styles.submitButton} ${
                    !isFormValid || isSubmitting ? styles.disabledButton : ""
                  }`}
                >
                  {isSubmitting ? (
                    <div className={styles.submitButtonContent}>
                      <div className={styles.submitSpinner}></div>
                      Submitting...
                    </div>
                  ) : (
                    "Submit"
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={LoginHandler}
                  disabled={!isFormValid || isSubmitting}
                  className={`${styles.submitButton} ${
                    !isFormValid || isSubmitting ? styles.disabledButton : ""
                  }`}
                >
                  {isSubmitting ? (
                    <div className={styles.submitButtonContent}>
                      <div className={styles.submitSpinner}></div>
                      Submitting...
                    </div>
                  ) : (
                    "Submit"
                  )}
                </button>
              )}

              <button
                type="button"
                onClick={resetForm}
                className={styles.resetButton}
              >
                Reset Form
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Exchange;
